import {
  getAdministratorClientId,
  getAllDevicesManagementAccess,
  getClientConfiguration,
  getClientInfo,
  getDeviceCheckAccess,
  getGeoEncodingAccess,
  getProjectManagementType,
  getSchedulingAccess,
  getTimeTrackingAccess,
  getVehicleManagementAccess,
  isMemberClient,
  isIresClient,
} from "../data/repository/ClientRepository";
import { ClientCapability } from "../data/models/ClientCapability";
import { ClientInfo } from "../data/models/ClientInfo";
import Client, { ProjectManagement } from "@/data/models/Client";
import User, { isDeviceManager } from "@/data/models/User";
import { isActiveUserIresAdminAccount } from "@/data/repository/UserRepository";
import { ClientModule } from "@/store/modules/ClientModule";

const IRES_CAPABILITIES = [
  ClientCapability.MANAGE_PROJECTS,
  ClientCapability.MANAGE_PROJECT_MANAGEMENT_SETTINGS,
  ClientCapability.MANAGE_HY_DRY_DEVICES,
  ClientCapability.MANAGE_ALL_DEVICES,
  ClientCapability.MANAGE_MESSAGES,

  ClientCapability.MANAGE_USERS,
  ClientCapability.MANAGE_TIME_TRACKING,
  ClientCapability.MANAGE_SCHEDULING,
  ClientCapability.MANAGE_VEHICLES,
  ClientCapability.MANAGE_DEVICES_CHECK,
  ClientCapability.MANAGE_ROLES,

  ClientCapability.VIEW_IRES_INFO,
  ClientCapability.VIEW_ADDITIONAL_PAUSE_DURATION_OPTIONS,
  ClientCapability.ALWAYS_SHOW_HY_DRY_DEVICE_STATUS,
  ClientCapability.CURRENT_STATUS_ALWAYS_VISIBLE_TO_USER,

  ClientCapability.VIEW_PROGRAM_CHAINS_OF_ALL_CLIENTS,
  ClientCapability.VIEW_PROGRAM_CHAINS_WITH_UNDEFINED_PROGRAM_NUMBER,
  ClientCapability.DEFINE_FINISHED_PROJECTS_SETTINGS
];

const ADMINISTRATION_CAPABILITIES = [
  ClientCapability.MANAGE_PROJECT_MANAGEMENT_SETTINGS,
  ClientCapability.MANAGE_HY_DRY_DEVICES,
  ClientCapability.MANAGE_USERS,
  ClientCapability.MANAGE_ROLES,
  ClientCapability.MANAGE_HY_DRY_MESSAGES_SETTINGS,

  ClientCapability.CONFIGURE_ADMINISTRATION_CAPABILITIES,
  ClientCapability.CONFIGURE_ADMINISTRATION_SETTINGS,

  ClientCapability.CLIENT_ADMINISTRATION,
  ClientCapability.DEVICE_ADMINISTRATION,
  ClientCapability.SETTINGS_ADMINISTRATION,

  ClientCapability.USER_ADMINISTRATION,
  ClientCapability.LICENCE_ADMINISTRATION,

  ClientCapability.DEFINE_CATEGORIES_AND_TEMPLATES,
  ClientCapability.DEFINE_OWN_ROLE_SETTINGS,
  ClientCapability.DEFINE_SYSTEM_CONFIGURATION,
  ClientCapability.DEFINE_OWN_REPORT_SETTINGS,
  ClientCapability.DEFINE_REPORT_SETTINGS_FOR_GROUP,
  ClientCapability.DEFINE_OWN_USER_SETTINGS,
  ClientCapability.DEFINE_OWN_COMPANY_SETTINGS,
  ClientCapability.DEFINE_OWN_HY_DRY_DEVICE_SETTINGS,
  ClientCapability.DEFINE_OWN_PROJECT_MANAGEMENT_SETTINGS,
  ClientCapability.DEFINE_PROJECT_MANAGEMENT_SETTINGS_FOR_GROUP,
  ClientCapability.DEFINE_OWN_ALL_DEVICES_SETTINGS,
  ClientCapability.DEFINE_OWN_HY_DRY_MESSAGE_SETTINGS,
  ClientCapability.DEFINE_FINISHED_PROJECTS_SETTINGS,

  ClientCapability.SWITCH_CLIENTS,
  
  ClientCapability.VIEW_ADMINISTRATOR_CLIENT_SETTINGS,
  ClientCapability.VIEW_EXTERNAL_SYSTEM,
];

const OPERATIONAL_CAPABILITIES = [
  ClientCapability.MANAGE_PROJECTS,
  ClientCapability.MANAGE_PROJECT_MANAGEMENT_SETTINGS,
  ClientCapability.MANAGE_HY_DRY_DEVICES,
  ClientCapability.MANAGE_ALL_DEVICES,
  ClientCapability.MANAGE_MESSAGES,

  ClientCapability.MANAGE_USERS,
  ClientCapability.MANAGE_ROLES,
  ClientCapability.MANAGE_BRANCHES,
  // Released to clients on 2026-09-07 (ticket 2609016), alongside the user-level GEO_ENCODING.
  // It belongs here rather than with the IRES capabilities because it is the client-level half of
  // the address pin and picker, which sit on the very records the neighbours above manage: a
  // branch, a user, a project's damage location. What a client actually sees is still decided by
  // KARTE and by its own `Adressen geocodieren` switch, which is off until asked for.
  //
  // The standalone Karte screen is NOT part of this: it has its own `VIEW_MAP_PAGE`, which stays
  // IRES-only because that screen is still under development.
  ClientCapability.MANAGE_GEO_ENCODING,
];

const ADMINISTRATION_AND_OPERATIONAL_CAPABILITIES: ClientCapability[] = [];

function hasCapability(
  clientInfo: ClientInfo,
  clientCapability: ClientCapability
) {
  const hasAdministrationCapabilities = clientInfo.hasAdministrationCapabilities ?? false;
  const hasOperationalCapabilities = clientInfo.isOperationalClient !== false;

  const hasIresCapabilities =
    (isIresClient() || isActiveUserIresAdminAccount()) &&
    !hasAdministrationCapabilities;

  if (IRES_CAPABILITIES.includes(clientCapability) && hasIresCapabilities) {
    return true;
  } else if (
    ADMINISTRATION_AND_OPERATIONAL_CAPABILITIES.includes(clientCapability) &&
    hasAdministrationCapabilities &&
    hasOperationalCapabilities
  ) {
    return true;
  } else if (
    ADMINISTRATION_CAPABILITIES.includes(clientCapability) &&
    hasAdministrationCapabilities
  ) {
    return true;
  } else if (
    OPERATIONAL_CAPABILITIES.includes(clientCapability) &&
    hasOperationalCapabilities
  ) {
    return true;
  }

  return false;
}

function doesClientHaveCapability(...clientCapabilities: ClientCapability[]) {
  const clientInfo = getClientInfo();

  if (!clientInfo) {
    return false;
  }

  return clientCapabilities.some((clientCapability) =>
    hasCapability(clientInfo, clientCapability)
  );
}

export function isDevicesOnlyWebsite() {
  const projectManagementType = getProjectManagementType();

  return projectManagementType === ProjectManagement.DEVICES_ONLY_WEBSITE;
}

/**
 * Whether the signed-in client is an IRES admin-portal client.
 *
 * Portal-shape switch in the same spirit as {@link isDevicesOnlyWebsite}: the admin portal is an
 * administration tenant, so project-, customer- and billing-oriented features are switched off for
 * it rather than duplicated into a separate component tree.
 *
 * Reads the value `resolveOfficeAdminPortalClient()` cached in `ClientModule`, because the
 * underlying allow-list (`administration/adminConfig.officeAdminClientIds`) can only be read
 * asynchronously while these access functions must stay synchronous.
 *
 * **Not an access boundary.** The portal's real gate is the async check in `router.beforeEach`
 * via `ADMIN_PORTAL_COMPONENTS`; this only decides what is offered.
 */
export function isOfficeAdminPortalClient() {
  return ClientModule.isOfficeAdminPortalClient;
}

export function manageProjectsClientAccess(activeUser: User | null) {
  return (
    !isDevicesOnlyWebsite() &&
    !isDeviceManager(activeUser) &&
    doesClientHaveCapability(ClientCapability.MANAGE_PROJECTS)
  );
}

export function manageProjectManagementSettingsClientAccess(activeUser: User | null) {
  return (
    !isDevicesOnlyWebsite() &&
    !isDeviceManager(activeUser) &&
    doesClientHaveCapability(ClientCapability.MANAGE_PROJECT_MANAGEMENT_SETTINGS)
  );
}

export function manageHyDryDevicesClientAccess() {
  return doesClientHaveCapability(ClientCapability.MANAGE_HY_DRY_DEVICES);
}

export function manageAllDevicesClientAccess() {
  return (
    getAllDevicesManagementAccess() &&
    doesClientHaveCapability(ClientCapability.MANAGE_ALL_DEVICES)
  );
}

/**
 * Whether the client gets the "Alle Geräte" settings that define generic devices (Gerätekategorien,
 * Projektgeräte-Einstellungen): while it works with generic devices ("Alle Geräte verwalten" on), or
 * when it is a group's MASTER-Mandant, which defines them for its members whatever its own switch
 * says. A MASTER-Mandant never holds `MANAGE_ALL_DEVICES`, so the switch alone would lock it out.
 */
export function allDevicesSettingsClientAccess() {
  return (
    manageAllDevicesClientAccess() ||
    doesClientHaveCapability(ClientCapability.DEFINE_CATEGORIES_AND_TEMPLATES)
  );
}

export function manageMessagesClientAccess() {
  return (
    !ClientModule.isAdministratorClientUser &&
    doesClientHaveCapability(ClientCapability.MANAGE_MESSAGES)
  );
}

export function manageHyDryMessagesSettingsClientAccess() {
  return doesClientHaveCapability(ClientCapability.MANAGE_HY_DRY_MESSAGES_SETTINGS);
}

export function manageUsersClientAccess() {
  return (
    doesClientHaveCapability(ClientCapability.MANAGE_USERS)
  );
}

export function manageTimeTrackingClientAccess() {
  return (
    !isDevicesOnlyWebsite() &&
    getTimeTrackingAccess() &&
    doesClientHaveCapability(ClientCapability.MANAGE_TIME_TRACKING)
  );
}

export function manageSchedulingClientAccess() {
  return (
    !isDevicesOnlyWebsite() &&
    getSchedulingAccess() &&
    doesClientHaveCapability(ClientCapability.MANAGE_SCHEDULING)
  );
}

export function manageVehiclesClientAccess() {
  return (
    !isDevicesOnlyWebsite() &&
    getVehicleManagementAccess() &&
    doesClientHaveCapability(ClientCapability.MANAGE_VEHICLES)
  );
}

export function manageDeviceCheckAccess() {
  return (
    getDeviceCheckAccess() &&
    doesClientHaveCapability(ClientCapability.MANAGE_DEVICES_CHECK)
  );
}

export function manageRolesClientAccess() {
  return (
    !isDevicesOnlyWebsite() &&
    doesClientHaveCapability(ClientCapability.MANAGE_ROLES)
  );
}

export function manageBranchesClientAccess() {
  return (
    doesClientHaveCapability(ClientCapability.MANAGE_BRANCHES)
  );
}

export function manageGeoEncodingClientAccess() {
  return (
    !isDevicesOnlyWebsite() &&
    getGeoEncodingAccess() &&
    doesClientHaveCapability(ClientCapability.MANAGE_GEO_ENCODING)
  );
}

export function viewTestHyDryDevicesClientAccess() {
  return doesClientHaveCapability(
    ClientCapability.VIEW_TEST_HY_DRY_DEVICES
  );
}

export function viewIresInfoClientAccess() {
  return doesClientHaveCapability(
    ClientCapability.VIEW_IRES_INFO
  );
}

export function viewAdditionalPauseDurationOptionsClientAccess() {
  return doesClientHaveCapability(
    ClientCapability.VIEW_ADDITIONAL_PAUSE_DURATION_OPTIONS
  );
}

export function alwaysShowHyDryDeviceStatusClientAccess() {
  return doesClientHaveCapability(
    ClientCapability.ALWAYS_SHOW_HY_DRY_DEVICE_STATUS
  );
}

export function currentStatusAlwaysVisibleToUserClientAccess() {
  return doesClientHaveCapability(
    ClientCapability.CURRENT_STATUS_ALWAYS_VISIBLE_TO_USER
  );
}

export function viewProgramChainsOfAllClientsClientAccess() {
  return doesClientHaveCapability(
    ClientCapability.VIEW_PROGRAM_CHAINS_OF_ALL_CLIENTS
  );
}

export function viewProgramChainsWithUndefinedProgramNumber() {
  return doesClientHaveCapability(
    ClientCapability.VIEW_PROGRAM_CHAINS_WITH_UNDEFINED_PROGRAM_NUMBER
  );
}

export function configureAdministrationCapabilitiesClientAccess() {
  return doesClientHaveCapability(
    ClientCapability.CONFIGURE_ADMINISTRATION_CAPABILITIES
  );
}

export function configureAdminsitrationSettingsClientAccess() {
  return doesClientHaveCapability(
    ClientCapability.CONFIGURE_ADMINISTRATION_SETTINGS
  );
}

export function defineOwnRoleSettingsClientAccess(enforceUsingGlobalSettins = false) {
  // When a Master user is logged in to the Member client, we should use the global role settings
  // for this user.
  if (ClientModule.isAdministratorClientUser && enforceUsingGlobalSettins) {
    return false;
  }

  // Member clients must always use the master's role settings to prevent licence abuse.
  // Cloud-side enforcement is already in place (LicencesV3.ts), this ensures UI consistency.
  if (isMemberClient()) {
    return false;
  }

  return (
    getClientConfiguration().defineOwnRoleSettings !== false ||
    doesClientHaveCapability(
      ClientCapability.DEFINE_OWN_ROLE_SETTINGS
    )
  );
}

export function defineOwnFinishedProjectsSettingsClientAccess() {
  return (
    getClientConfiguration().defineOwnSystemConfiguration !== false &&
    doesClientHaveCapability(
      ClientCapability.DEFINE_FINISHED_PROJECTS_SETTINGS
    )
  );
}

export function defineOwnSystemConfigurationClientAccess() {
  return (
    getClientConfiguration().defineOwnSystemConfiguration !== false ||
    doesClientHaveCapability(
      ClientCapability.DEFINE_SYSTEM_CONFIGURATION
    )
  );
}

export function viewExternalSystemsClientAccess() {
  return (
    doesClientHaveCapability(
      ClientCapability.VIEW_EXTERNAL_SYSTEM
    )
  );
}

export function defineOwnReportSettingsClientAccess() {
  return (
    getClientConfiguration().defineOwnReportSettings !== false ||
    doesClientHaveCapability(
      ClientCapability.DEFINE_OWN_REPORT_SETTINGS
    )
  );
}

export function defineOwnProjectManagementSettingsClientAccess() {
  return (
    getClientConfiguration().defineOwnProjectManagementSettings !== false ||
    doesClientHaveCapability(
      ClientCapability.DEFINE_OWN_PROJECT_MANAGEMENT_SETTINGS
    )
  );
}

export function defineOwnAllDevicesSettingsClientAccess() {
  return (
    getClientConfiguration().defineOwnAllDevicesSettings !== false ||
    doesClientHaveCapability(
      ClientCapability.DEFINE_OWN_ALL_DEVICES_SETTINGS
    )
  );
}

export function defineOwnUserSettingsClientAccess() {
  return (
    getClientConfiguration().defineOwnUserSettings !== false ||
    doesClientHaveCapability(
      ClientCapability.DEFINE_OWN_USER_SETTINGS
    )
  );
}

export function canMemberDefineOwnUserSettings(memberClient: Client) {
  return (
    memberClient.clientConfiguration?.defineOwnUserSettings !== false
  );
}

export function defineOwnHyDryMessagesSettingsClientAccess() {
  return (
    getClientConfiguration().defineOwnHyDryMessagesSettings !== false ||
    doesClientHaveCapability(
      ClientCapability.DEFINE_OWN_HY_DRY_MESSAGE_SETTINGS
    )
  );
}

export function defineOwnHyDryDeviceSettingsClientAccess() {
  return (
    getClientConfiguration().defineOwnHyDryDeviceSettings !== false ||
    doesClientHaveCapability(
      ClientCapability.DEFINE_OWN_HY_DRY_DEVICE_SETTINGS
    )
  );
}

export function defineOwnCompanySettingsClientAccess() {
  return (
    getClientConfiguration().defineOwnCompanySettings !== false ||
    doesClientHaveCapability(
      ClientCapability.DEFINE_OWN_COMPANY_SETTINGS
    )
  );
}

export function defineSystemConfigurationForGroupClientAccess() {
  return (
    doesClientHaveCapability(
      ClientCapability.DEFINE_SYSTEM_CONFIGURATION
    )
  );
}

export function defineCategoriesAndTemplatesClientAccess() {
  return (
    !getAdministratorClientId() ||
    getClientConfiguration().defineOwnAllDevicesSettings !== false ||
    doesClientHaveCapability(
      ClientCapability.DEFINE_CATEGORIES_AND_TEMPLATES
    )
  );
}

export function defineReportSettingsForGroupClientAccess() {
  return (
    doesClientHaveCapability(
      ClientCapability.DEFINE_REPORT_SETTINGS_FOR_GROUP
    )
  );
}

export function defineProjectManagementSettingsForGroupClientAccess() {
  return (
    doesClientHaveCapability(
      ClientCapability.DEFINE_PROJECT_MANAGEMENT_SETTINGS_FOR_GROUP
    )
  );
}

export function clientAdministrationAccess() {
  return doesClientHaveCapability(
    ClientCapability.CLIENT_ADMINISTRATION
  );
}

export function deviceAdministrationAccess() {
  return doesClientHaveCapability(
    ClientCapability.DEVICE_ADMINISTRATION
  );
}

export function settingsAdministrationAccess() {
  return doesClientHaveCapability(
    ClientCapability.SETTINGS_ADMINISTRATION
  );
}

export function userAdministrationAccess() {
  return doesClientHaveCapability(
    ClientCapability.USER_ADMINISTRATION
  );
}

export function licenceAdministrationAccess() {
  return doesClientHaveCapability(
    ClientCapability.LICENCE_ADMINISTRATION
  );
}

export function switchClientsAccess() {
  return doesClientHaveCapability(
    ClientCapability.SWITCH_CLIENTS
  );
}

export function administratorClientSettingsAccess() {
  return doesClientHaveCapability(
    ClientCapability.VIEW_ADMINISTRATOR_CLIENT_SETTINGS
  );
}